import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import toolkitService from '../../services/ToolkitService';
import Button from '../shared/Button';

// Code mappings, v5 (or source) value → Opera Cloud value, kept per property in toolkit storage (config `configKey`)
// and edited here — shared by Block Grid Update (room types) and Event Management (event codes).
//
// The value is { [category]: { [from]: to } }. With one category the tabs are hidden. `seed` (optional) is
// { [category]: [from…] } offered as identity mappings for categories still empty; `extra` renders beside the buttons
// (e.g. "Load from Opera Cloud DVM"). onChange(value) reports every saved change to the screen.

function ToolkitMappingEditor({ ctx, ready, configKey, categories, seed, onChange, extra, external }) {
    const [value, setValue] = useState({});
    const [cat, setCat] = useState(categories[0].key);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [msg, setMsg] = useState('');
    const [dirty, setDirty] = useState(false);
    // The latest onChange, so loading the saved mappings does not re-run when the parent re-renders.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (!ready) return;
        toolkitService.storage(ctx, 'get-config', { configKey })
            .then(({ data }) => { const v = data.result?.value || {}; setValue(v); if (onChangeRef.current) onChangeRef.current(v); })
            .catch(() => setMsg('Could not load the saved mappings.'));
    }, [ctx, ready, configKey]);

    // A screen can hand in mappings fetched from elsewhere (Block Grid's DVM fetch): merged in, unsaved until Save.
    useEffect(() => {
        if (!external) return;
        setValue((v) => ({ ...v, [external.category]: { ...(v[external.category] || {}), ...external.mappings } }));
        setDirty(true);
    }, [external]);

    const set = (next) => { setValue(next); setDirty(true); };
    const rows = useMemo(() => Object.entries(value[cat] || {}).sort(([a], [b]) => a.localeCompare(b)), [value, cat]);

    const save = async () => {
        try {
            await toolkitService.storage(ctx, 'save-config', { configKey, value });
            setDirty(false);
            setMsg('Saved.');
            onChange && onChange(value);
        } catch (err) {
            setMsg(err?.response?.data?.message || err.message);
        }
    };
    const seedEmpty = () => {
        const next = { ...value };
        for (const c of categories) {
            if (Object.keys(next[c.key] || {}).length) continue;
            next[c.key] = Object.fromEntries((seed?.[c.key] || []).map((s) => [s, s]));
        }
        set(next);
    };

    return (
        <div>
            {categories.length > 1 && (
                <div className="tkb-tabs">
                    {categories.map((c) => (
                        <button key={c.key} type="button" className={`tkb-tab${cat === c.key ? ' tkb-tab-active' : ''}`} onClick={() => setCat(c.key)}>
                            {c.label} <span className="tkb-desc">({Object.keys(value[c.key] || {}).length})</span>
                        </button>
                    ))}
                </div>
            )}
            <div className="tkb-row" style={{ marginBottom: '0.6rem' }}>
                <label><span>From</span><input type="text" value={from} onChange={(e) => setFrom(e.target.value.toUpperCase())} style={{ width: 140 }} /></label>
                <label><span>To (Opera Cloud)</span><input type="text" value={to} onChange={(e) => setTo(e.target.value.toUpperCase())} style={{ width: 140 }} /></label>
                <Button variant="secondary" disabled={!from.trim() || !to.trim()}
                    onClick={() => { set({ ...value, [cat]: { ...(value[cat] || {}), [from.trim()]: to.trim() } }); setFrom(''); setTo(''); }}>
                    <Plus className="icon" /> Add
                </Button>
                {seed && <Button variant="secondary" onClick={seedEmpty}>Seed empty categories</Button>}
                {extra}
                <Button variant="success" onClick={save} disabled={!ready || !dirty}><Save className="icon" /> Save mappings</Button>
            </div>
            {msg && <p className="tkb-msg">{msg}</p>}
            <div className="otx-table-wrap" style={{ maxHeight: 320, overflow: 'auto' }}>
                <table className="otx-table">
                    <thead><tr><th>From</th><th>To (Opera Cloud)</th><th /></tr></thead>
                    <tbody>
                        {rows.map(([k, v]) => (
                            <tr key={k}>
                                <td>{k}</td>
                                <td><input type="text" className="tkb-cell-input" value={v}
                                    onChange={(e) => set({ ...value, [cat]: { ...value[cat], [k]: e.target.value.toUpperCase() } })} /></td>
                                <td className="tkb-actions">
                                    <button type="button" className="tkb-icon-btn" title="Remove"
                                        onClick={() => set({ ...value, [cat]: Object.fromEntries(Object.entries(value[cat]).filter(([x]) => x !== k)) })}>
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!rows.length && <tr><td colSpan={3} className="otx-empty">No mappings yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ToolkitMappingEditor;
